import { useEffect, useRef, useState } from 'react';
import { captureFrame } from './useCamera.js';

/**
 * Leitura continua de codigo de barras e QR a partir de um <video> ja aberto.
 *
 * Dois motores, nesta ordem:
 *
 *   1. `BarcodeDetector` — nativo do navegador (Chrome/Edge/Android). Roda no
 *      processo do browser, le direto do elemento de video e custa quase nada
 *      de bateria, que importa num tablet de portaria ligado o dia todo.
 *   2. ZXing em WebAssembly — carregado sob demanda so quando o nativo nao
 *      existe. E o caso do Safari/iOS, que a portaria usa quando o porteiro
 *      abre pelo proprio iPhone.
 *
 * O ZXing entra por `import()` dinamico de proposito: sao ~300KB que nao devem
 * pesar no primeiro carregamento de quem nunca vai precisar deles.
 */

export type ScannerEngine = 'native' | 'zxing' | 'none';

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

/** Formatos que aparecem em etiqueta de transportadora no Brasil. */
const WANTED_FORMATS = [
  'qr_code',
  'code_128', // padrao das etiquetas dos Correios e da maioria das transportadoras
  'code_39',
  'itf', // usado na chave da NF-e
  'ean_13',
  'data_matrix', // Mercado Livre
  'pdf417',
];

function nativeConstructor(): BarcodeDetectorConstructor | null {
  const candidate = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  return typeof candidate === 'function' ? candidate : null;
}

interface Options {
  video: React.RefObject<HTMLVideoElement | null>;
  /** Liga e desliga o loop sem fechar a camera. */
  active: boolean;
  onDecode: (raw: string) => void;
}

export function useBarcodeScanner({ video, active, onDecode }: Options): {
  engine: ScannerEngine;
} {
  const [engine, setEngine] = useState<ScannerEngine>('none');

  // O callback entra por ref para que trocar a funcao (o que acontece a cada
  // render da pagina) nao derrube e recrie o loop de leitura.
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;

  // Um codigo fica varios frames na frente da camera. Sem esta trava o mesmo
  // pacote dispararia dezenas de leituras por segundo.
  const lastRef = useRef<{ value: string; at: number } | null>(null);

  useEffect(() => {
    if (!active) return;

    let stopped = false;
    let timer: number | undefined;

    function emit(raw: string) {
      const value = raw.trim();
      if (!value) return;

      const last = lastRef.current;
      const now = Date.now();
      if (last && last.value === value && now - last.at < 2500) return;

      lastRef.current = { value, at: now };
      onDecodeRef.current(value);
    }

    async function run() {
      const native = nativeConstructor();

      if (native) {
        setEngine('native');

        // Pedir um formato que o navegador nao suporta faz o construtor
        // lancar; por isso a interseccao com a lista dele.
        let supported = WANTED_FORMATS;
        try {
          const available = (await native.getSupportedFormats?.()) ?? [];
          if (available.length > 0) {
            const filtered = WANTED_FORMATS.filter((format) => available.includes(format));
            if (filtered.length > 0) supported = filtered;
          }
        } catch {
          // Segue com a lista completa; o catch abaixo cobre o construtor.
        }

        let detector: BarcodeDetectorLike;
        try {
          detector = new native({ formats: supported });
        } catch {
          detector = new native();
        }

        const tick = async () => {
          if (stopped) return;
          const element = video.current;

          if (element && element.readyState >= 2) {
            try {
              const results = await detector.detect(element);
              for (const result of results) emit(result.rawValue);
            } catch {
              // Frame ruim (video trocando de resolucao, aba em background).
              // Erro por frame nao merece derrubar a leitura.
            }
          }

          if (!stopped) timer = window.setTimeout(() => void tick(), 200);
        };

        void tick();
        return;
      }

      // --- Fallback WebAssembly ---------------------------------------------
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      if (stopped) return;

      setEngine('zxing');
      const reader = new BrowserMultiFormatReader();

      const tick = () => {
        if (stopped) return;
        const element = video.current;

        if (element && element.readyState >= 2) {
          // 1024px e o ponto em que code_128 de etiqueta ainda decodifica e a
          // busca ainda cabe no orcamento de um frame em celular.
          const canvas = captureFrame(element, 1024);
          if (canvas) {
            try {
              emit(reader.decodeFromCanvas(canvas).getText());
            } catch {
              // NotFoundException a cada frame sem codigo — o caso normal.
            }
          }
        }

        if (!stopped) timer = window.setTimeout(tick, 350);
      };

      tick();
    }

    void run();

    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [active, video]);

  return { engine };
}
